import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useState } from 'react';
import {
  BillingExportDetailResponse,
  BillingExportRowDetailItem,
  BillingExportStatus,
  BillingRowStatus,
} from '../../interfaces/billing.async';
import { BillingService } from '../../services/billing';
import { SectionTitle } from '../common/SectionTitle';

interface BillingExportDetailProps {
  exportDetail: BillingExportDetailResponse;
  onClose: () => void;
  isLoading?: boolean;
}

type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error';

const getStatusColor = (status: string): ChipColor => {
  switch (status) {
    case BillingExportStatus.Pending:
    case BillingRowStatus.Pending:
      return 'default';
    case BillingExportStatus.Processing:
    case BillingRowStatus.Processing:
      return 'primary';
    case BillingExportStatus.Completed:
    case BillingRowStatus.Completed:
      return 'success';
    case BillingExportStatus.CompletedWithErrors:
      return 'warning';
    case BillingExportStatus.Failed:
    case BillingRowStatus.Error:
      return 'error';
    default:
      return 'default';
  }
};

const formatStatus = (status: string): string => {
  switch (status) {
    case BillingExportStatus.Pending:
    case BillingRowStatus.Pending:
      return 'Pending';
    case BillingExportStatus.Processing:
    case BillingRowStatus.Processing:
      return 'Processing';
    case BillingExportStatus.Completed:
    case BillingRowStatus.Completed:
      return 'Completed';
    case BillingExportStatus.CompletedWithErrors:
      return 'With Errors';
    case BillingExportStatus.Failed:
      return 'Failed';
    case BillingRowStatus.Error:
      return 'Error';
    default:
      return status;
  }
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const BillingExportDetail: FC<BillingExportDetailProps> = ({
  exportDetail,
  onClose,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<BillingExportRowDetailItem[]>([]);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  const fetchRows = async () => {
    setIsLoadingRows(true);

    try {
      const billingService = new BillingService();
      const response = await billingService.getBillingExportRows(exportDetail.requestId, {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(showDetails && { includeData: true }),
      });

      if (response) {
        setRows(response.rows);
        setTotalRows(response.pagination.total);
      }
    } catch (err) {
      console.error('Failed to load rows:', err);
    } finally {
      setIsLoadingRows(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [exportDetail.requestId, page, rowsPerPage, showDetails]);

  const handleDownloadOutput = () => {
    if (exportDetail.result?.outputFileUrl) {
      window.open(exportDetail.result.outputFileUrl, '_blank');
    }
  };

  const handleDownloadErrors = () => {
    if (exportDetail.result?.errorFileUrl) {
      window.open(exportDetail.result.errorFileUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={onClose} variant='text'>
            Back
          </Button>
          <SectionTitle
            label='Export Detail'
            variant='h5'
            style={{ fontWeight: '500', margin: 0 }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant='h4' fontWeight={600}>
                {exportDetail.totalRows}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Total Rows
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant='h4' fontWeight={600} color='success.main'>
                {exportDetail.completedRows}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Completed
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant='h4' fontWeight={600} color='error.main'>
                {exportDetail.errorRows}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Errors
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant='h4' fontWeight={600} color='warning.main'>
                {exportDetail.pendingRows}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Pending
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Details */}
        <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Status
              </Typography>
              <Chip
                label={formatStatus(exportDetail.status)}
                color={getStatusColor(exportDetail.status)}
                size='small'
                sx={{ mt: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <LinearProgress
                  variant='determinate'
                  value={exportDetail.progress}
                  sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                />
                <Typography variant='body2'>{Math.round(exportDetail.progress)}%</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Created
              </Typography>
              <Typography variant='body1'>{formatDate(exportDetail.createdAt)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Updated
              </Typography>
              <Typography variant='body1'>{formatDate(exportDetail.updatedAt)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                User
              </Typography>
              <Typography variant='body1'>{exportDetail.userEmail || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                HL7 Format
              </Typography>
              <Typography variant='body1'>
                {exportDetail.parameters?.hl7Format ? 'Yes' : 'No'}
              </Typography>
            </Grid>
          </Grid>

          {/* Error Message */}
          {exportDetail.errorMessage && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography variant='body2' color='error.dark'>
                {exportDetail.errorMessage}
              </Typography>
            </Box>
          )}

          {/* Download Buttons */}
          {(exportDetail.result?.outputFileUrl || exportDetail.result?.errorFileUrl) && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {exportDetail.result?.outputFileUrl && (
                <Button
                  variant='contained'
                  size='small'
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadOutput}>
                  Download Output
                </Button>
              )}
              {exportDetail.result?.errorFileUrl && (
                <Button
                  variant='outlined'
                  size='small'
                  color='error'
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadErrors}>
                  Download Errors
                </Button>
              )}
            </Box>
          )}
        </Paper>

        {/* Rows Table */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant='h6'>Rows</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showDetails}
                onChange={(e) => setShowDetails(e.target.checked)}
                size='small'
              />
            }
            label='Show details'
          />
        </Box>
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Row #</TableCell>
                {showDetails && <TableCell>Patient Name</TableCell>}
                {showDetails && <TableCell>Date of Service</TableCell>}
                {showDetails && <TableCell>CPT</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingRows ? (
                <TableRow>
                  <TableCell colSpan={showDetails ? 7 : 4} align='center' sx={{ py: 3 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showDetails ? 7 : 4} align='center' sx={{ py: 3 }}>
                    <Typography variant='body2' color='text.secondary'>
                      No rows found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const enrichedRow = row.enrichedData?.rows?.[0];
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.rowNumber}</TableCell>
                      {showDetails && <TableCell>{enrichedRow?.fullName || '-'}</TableCell>}
                      {showDetails && <TableCell>{enrichedRow?.dateOfService || '-'}</TableCell>}
                      {showDetails && <TableCell>{enrichedRow?.cpt || '-'}</TableCell>}
                      <TableCell>
                        <Chip
                          label={formatStatus(row.status)}
                          color={getStatusColor(row.status)}
                          size='small'
                        />
                      </TableCell>
                      <TableCell>
                        {row.errorMessage ? (
                          <Typography variant='body2' color='error'>
                            {row.errorMessage}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(row.updatedAt)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={totalRows}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </CardContent>
    </Card>
  );
};

export default BillingExportDetail;
